import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { useRatingNudgeStore } from '@/store/ratingNudge'
import { fetchPlayed, rateGame, removeFromPlayed, promoteToPlaying, promoteToPlayed } from '@/api/wishlist'
import { useDemoteToWishlist } from '@/hooks/useDemoteToWishlist'
import { useGroupPermissions } from '@/hooks/useGroupPermissions'
import { fetchGroupSummary } from '@/api/groups'
import { toFriend } from '@/lib/steam'
import { GroupHeader } from '@/components/groups/GroupHeader'
import { PlayedRow } from '@/components/played/PlayedRow'
import { AddGameModal } from '@/components/wishlist/AddGameModal'
import { Spinner } from '@/components/ui/Spinner'
import { Icon } from '@/components/ui/Icon'
import { ThemedSelect } from '@/components/ui/ThemedSelect'
import type { PlayedEntryWithGame } from '@/types'

type PlayedFilter = 'all' | 'unrated-me' | 'highly-rated'
type PlayedSort = 'finished' | 'rating-avg' | 'rating-me'

const SORT_OPTIONS: { value: PlayedSort; label: string }[] = [
  { value: 'finished', label: 'Recently finished' },
  { value: 'rating-avg', label: 'Highest rated (average)' },
  { value: 'rating-me', label: 'Highest rated (me)' },
]

const FILTER_OPTIONS: { value: PlayedFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unrated-me', label: 'Unrated by me' },
  { value: 'highly-rated', label: 'Highly rated' },
]

function averageRating(ratings: Record<string, number>): number | null {
  const values = Object.values(ratings)
  if (values.length === 0) return null
  return values.reduce((s, v) => s + v, 0) / values.length
}

export default function PlayedPage() {
  const { user } = useAuthStore()
  const { activeGroupId } = useUIStore()
  const { canEditContent } = useGroupPermissions()
  const suppressRatingNudge = useRatingNudgeStore((s) => s.suppressGame)
  const queryClient = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [searchParams] = useSearchParams()
  const initialFilter: PlayedFilter =
    searchParams.get('filter') === 'unrated-me' ? 'unrated-me' : 'all'
  const [filter, setFilter] = useState<PlayedFilter>(initialFilter)
  const [sortBy, setSortBy] = useState<PlayedSort>('finished')

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['played', activeGroupId],
    queryFn: () => fetchPlayed(activeGroupId!),
    enabled: !!activeGroupId,
  })

  const { data: summary } = useQuery({
    queryKey: ['group-summary', activeGroupId],
    queryFn: () => fetchGroupSummary(activeGroupId!),
    enabled: !!activeGroupId,
  })
  const groupMembers = (summary?.members ?? [])
    .map(toFriend)
    .sort((a, b) => (a.nickname || a.name).localeCompare(b.nickname || b.name))

  const rateMutation = useMutation({
    mutationFn: ({ gameId, rating }: { gameId: string; rating: number }) =>
      rateGame(activeGroupId!, gameId, rating),
    onMutate: async ({ gameId, rating }) => {
      // Removing a rating is deliberate — don't let the nudge banner re-prompt it.
      if (rating === 0 && activeGroupId) suppressRatingNudge(activeGroupId, gameId)
      await queryClient.cancelQueries({ queryKey: ['played', activeGroupId] })
      const previous = queryClient.getQueryData<PlayedEntryWithGame[]>(['played', activeGroupId])
      queryClient.setQueryData<PlayedEntryWithGame[]>(['played', activeGroupId], (old = []) =>
        old.map((entry) => {
          if (entry.gameId !== gameId) return entry
          const ratings = { ...entry.ratings }
          if (rating === 0) delete ratings[user!.steamId]
          else ratings[user!.steamId] = rating
          return { ...entry, ratings }
        })
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['played', activeGroupId], context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] }),
  })

  const removeMutation = useMutation({
    mutationFn: (gameId: string) => removeFromPlayed(activeGroupId!, gameId),
    onMutate: async (gameId) => {
      await queryClient.cancelQueries({ queryKey: ['played', activeGroupId] })
      const previous = queryClient.getQueryData<PlayedEntryWithGame[]>(['played', activeGroupId])
      queryClient.setQueryData<PlayedEntryWithGame[]>(['played', activeGroupId], (old = []) =>
        old.filter((e) => e.gameId !== gameId)
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['played', activeGroupId], context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] }),
  })

  const demoteToWishlistMutation = useDemoteToWishlist(activeGroupId!)

  const addToPlayedMutation = useMutation({
    mutationFn: (gameId: string) => promoteToPlayed(activeGroupId!, gameId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['wishlist', activeGroupId] })
      setShowAddModal(false)
    },
  })

  const promoteToPlayingMutation = useMutation({
    mutationFn: (gameId: string) => promoteToPlaying(activeGroupId!, gameId),
    onMutate: async (gameId) => {
      await queryClient.cancelQueries({ queryKey: ['played', activeGroupId] })
      const previous = queryClient.getQueryData<PlayedEntryWithGame[]>(['played', activeGroupId])
      queryClient.setQueryData<PlayedEntryWithGame[]>(['played', activeGroupId], (old = []) =>
        old.filter((e) => e.gameId !== gameId)
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['played', activeGroupId], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['played', activeGroupId] })
      queryClient.invalidateQueries({ queryKey: ['playing', activeGroupId] })
    },
  })

  const visibleEntries = useMemo(() => {
    const myId = user?.steamId
    const filtered = entries.filter((entry) => {
      if (filter === 'unrated-me') {
        return myId ? !entry.ratings[myId] : true
      }
      if (filter === 'highly-rated') {
        const avg = averageRating(entry.ratings)
        return avg !== null && avg >= 4
      }
      return true
    })
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'rating-avg': {
          const avgA = averageRating(a.ratings) ?? -1
          const avgB = averageRating(b.ratings) ?? -1
          if (avgB !== avgA) return avgB - avgA
          break
        }
        case 'rating-me': {
          const mine = myId ?? ''
          const ra = a.ratings[mine] ?? -1
          const rb = b.ratings[mine] ?? -1
          if (rb !== ra) return rb - ra
          break
        }
      }
      // Tie-breaker (and primary for 'finished'): most recently finished first
      const fa = a.finishedAt ? Date.parse(a.finishedAt) : 0
      const fb = b.finishedAt ? Date.parse(b.finishedAt) : 0
      return fb - fa
    })
    return sorted
  }, [entries, filter, sortBy, user?.steamId])

  if (!activeGroupId) {
    return (
      <div className="cg-screen">
        <div className="cg-empty">Select or create a group to see played games.</div>
      </div>
    )
  }

  return (
    <div className="cg-screen">
      <GroupHeader
        sublabel={entries.length > 0 ? `${entries.length} game${entries.length !== 1 ? 's' : ''}` : undefined}
      />

      <div className="cg-toolbar">
        <div className="cg-filter-group">
          <span className="cg-filter-label">Show</span>
          <div className="cg-segment">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className="cg-seg-btn"
                data-active={filter === opt.value}
                onClick={() => setFilter(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="cg-toolbar-divider" />

        <div className="cg-filter-group">
          <span className="cg-filter-label">Sort</span>
          <ThemedSelect
            value={sortBy}
            options={SORT_OPTIONS}
            onChange={setSortBy}
            ariaLabel="Sort played games"
          />
        </div>

        <div className="cg-toolbar-spacer" />
        {canEditContent && (
          <button className="cg-btn cg-btn-primary" onClick={() => setShowAddModal(true)}>
            <Icon name="plus" size={12} /> Add game
          </button>
        )}
      </div>

      {isLoading && <Spinner />}

      {!isLoading && entries.length === 0 && (
        <div className="cg-empty">
          {canEditContent ? (
            <>
              No games completed yet —{' '}
              <button
                style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 'inherit' }}
                onClick={() => setShowAddModal(true)}
              >
                add one!
              </button>
            </>
          ) : (
            'No games completed yet.'
          )}
        </div>
      )}

      {!isLoading && entries.length > 0 && visibleEntries.length === 0 && (
        <div className="cg-empty">No games match the current filters.</div>
      )}

      {!isLoading && visibleEntries.length > 0 && (
        <div className="cg-played-list">
          {visibleEntries.map((entry) => (
            <PlayedRow
              key={entry.gameId}
              entry={entry}
              groupMembers={groupMembers}
              currentUser={user!}
              onRate={(rating) => rateMutation.mutate({ gameId: entry.gameId, rating })}
              onRemove={() => removeMutation.mutate(entry.gameId)}
              onDemoteToWishlist={() => demoteToWishlistMutation.mutate(entry.gameId)}
              onPromoteToPlaying={() => promoteToPlayingMutation.mutate(entry.gameId)}
              canEdit={canEditContent}
            />
          ))}
        </div>
      )}
      {showAddModal && activeGroupId && (
        <AddGameModal
          groupId={activeGroupId}
          existingGameIds={new Set(entries.map((e) => e.gameId))}
          existingLabel="Already played"
          onClose={() => setShowAddModal(false)}
          onAdd={(gameId) => addToPlayedMutation.mutate(gameId)}
          isAdding={addToPlayedMutation.isPending}
          title="Add to played"
        />
      )}
    </div>
  )
}
